import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Trash2, FileDown } from "lucide-react";
import { http, formatSoles, STATUS_OPTIONS, statusLabel } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Confirm, Pill, ORDER_STATUS_COLORS, fmtDateTime, exportXlsx } from "./ui";
import { orderPdf } from "./docs";

export default function OrdersPage() {
  const { settings } = useStore();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sel, setSel] = useState([]);
  const load = () => http.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const list = useMemo(() => orders.filter((o) => {
    const t = q.toLowerCase().trim();
    if (t && !`${o.order_code} ${o.customer_name} ${o.customer_phone}`.toLowerCase().includes(t)) return false;
    if (status && o.status !== status) return false;
    const d = o.created_at.slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }), [orders, q, status, from, to]);

  async function bulk(action, value) {
    await http.post("/orders/bulk", { ids: sel, action, value });
    toast.success("Acción aplicada");
    setSel([]);
    load();
  }
  const allSel = list.length > 0 && list.every((o) => sel.includes(o.id));

  function exportRows(rows) {
    exportXlsx(rows.map((o) => ({
      Pedido: o.order_code, Fecha: fmtDateTime(o.created_at), Cliente: o.customer_name, Teléfono: o.customer_phone,
      Distrito: o.customer_district || "", Productos: o.items.map((i) => `${i.qty}x ${i.name}`).join("; "),
      Subtotal: o.subtotal ?? o.total, Descuento: o.discount || 0, Envío: o.shipping_cost || 0, Total: o.total,
      Pagado: o.paid || 0, Saldo: o.balance ?? o.total, Estado: statusLabel(o.status), "Fecha entrega": o.delivery_date || "",
    })), "pedidos-floral-rosse", "Pedidos");
  }

  return (
    <div data-testid="admin-orders-page">
      <PageHeader title="Pedidos" subtitle={`${orders.length} pedidos registrados`}
        actions={<Button variant="outline" onClick={() => exportRows(list)} data-testid="export-orders"><Download size={14} className="mr-1" /> Excel</Button>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por N° de pedido, nombre o teléfono" className="pl-9 bg-white" data-testid="order-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="order-filter-status">
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto bg-white" data-testid="order-filter-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto bg-white" data-testid="order-filter-to" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white rounded-xl border border-black/5 p-2" data-testid="order-bulk-bar">
        <Checkbox checked={allSel} onCheckedChange={(v) => setSel(v ? list.map((o) => o.id) : [])} data-testid="select-all-orders" />
        <span className="text-sm font-semibold mr-2">{sel.length} seleccionados</span>
        {sel.length > 0 && <>
          <select defaultValue="" onChange={(e) => e.target.value && bulk("status", e.target.value)} className="h-8 rounded-md border border-input px-2 text-xs bg-white" data-testid="bulk-order-status">
            <option value="">Cambiar estado a…</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => exportRows(orders.filter((o) => sel.includes(o.id)))}><Download size={12} className="mr-1" /> Excel</Button>
          <Button size="sm" variant="outline" onClick={() => orders.filter((o) => sel.includes(o.id)).forEach((o) => orderPdf(o, settings))} data-testid="bulk-order-pdf"><FileDown size={12} className="mr-1" /> PDF</Button>
          <Confirm onConfirm={() => bulk("delete")} title={`¿Eliminar ${sel.length} pedidos?`} trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid="bulk-order-delete"><Trash2 size={12} className="mr-1" /> Eliminar</Button>} />
        </>}
      </div>
      <div className="bg-white rounded-2xl border border-black/5 divide-y">
        {list.length === 0 && <p className="text-sm text-neutral-500 p-8 text-center">No hay pedidos con esos filtros.</p>}
        {list.map((o) => (
          <div key={o.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-neutral-50" data-testid={`order-row-${o.order_code}`}>
            <Checkbox checked={sel.includes(o.id)} onCheckedChange={(v) => setSel((s) => (v ? [...s, o.id] : s.filter((x) => x !== o.id)))} data-testid={`select-order-${o.order_code}`} />
            <Link to={`/admin/pedidos/${o.id}`} className="flex-1 grid grid-cols-2 sm:grid-cols-6 gap-2 items-center" data-testid={`open-order-${o.order_code}`}>
              <div className="font-serif text-lg font-bold text-[var(--brand-primary)]">{o.order_code}</div>
              <div className="sm:col-span-2 min-w-0">
                <div className="font-semibold truncate">{o.customer_name}</div>
                <div className="text-xs text-neutral-500">{o.customer_phone}</div>
              </div>
              <div className="text-xs text-neutral-500">{fmtDateTime(o.created_at)}</div>
              <div className="text-right sm:text-left">
                <div className="font-semibold">{formatSoles(o.total)}</div>
                {(o.balance ?? o.total) > 0 && o.status !== "cancelado" && <div className="text-[11px] text-amber-700">Saldo {formatSoles(o.balance ?? o.total)}</div>}
              </div>
              <div className="flex flex-wrap gap-1 justify-end sm:justify-start">
                <Pill className={ORDER_STATUS_COLORS[o.status]}>{statusLabel(o.status)}</Pill>
                {o.receipt_path && (o.receipt_status || "pendiente") === "pendiente" && <Pill className="bg-sky-100 text-sky-800">Comprobante</Pill>}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
